import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState, store } from '../../redux/store';
import { UserInfo, Login } from './types';
import { okaoAxios } from '../../helpers/axios';



export interface ISecureSlice {
  accessToken: string | undefined
  user: UserInfo | undefined
  login: Login | undefined
}

// Storage key for the entire secure state
const SECURE_STORAGE_KEY = 'resactly-admin-auth';

// Helper functions for secure state persistence
export const loadSecureStateFromStorage = (): ISecureSlice | undefined => {
  try {
    const raw = localStorage.getItem(SECURE_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as ISecureSlice;
    return parsed;
  } catch (error) {
    console.error('Failed to load secure state from storage', error);
    return undefined;
  }
};

export const saveSecureStateToStorage = (state: ISecureSlice): void => {
  try {
    localStorage.setItem(SECURE_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save secure state to storage', error);
  }
};

export const clearSecureStateFromStorage = (): void => {
  try {
    localStorage.removeItem(SECURE_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear secure state from storage', error);
  }
};

// Initialize state with data from localStorage if available
// Use try-catch to handle cases where localStorage might not be available (e.g., in tests)
let persistedState: ISecureSlice | undefined;
try {
  persistedState = loadSecureStateFromStorage();
} catch (error) {
  // In test environments, this might fail due to circular dependencies
  // Fall back to undefined
  persistedState = undefined;
}
const initialState: ISecureSlice = persistedState ?? {
  accessToken: undefined,
  user: undefined,
  login: undefined,
};

export const getUserInfo = createAsyncThunk('api/getUserInfo', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).get(`https://${process.env.REACT_APP_AUTH0_DOMAIN??''}/userinfo`)
  return response;
})

export const loginAudit = createAsyncThunk('api/loginAudit', async () => {
  const auth = store.getState().secure.accessToken ?? '';
  const response = await okaoAxios(auth).post(`${process.env.REACT_APP_DIS_URL ?? window.location.origin}/login/admin`)
  return response;
})

export const secureSlice = createSlice({
  name: 'secure',
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string | undefined>) => {
      state.accessToken = action.payload;
      // Persist full state to localStorage
      saveSecureStateToStorage(state);
    },
    setUserInfo: (state, action: PayloadAction<UserInfo | undefined>) => {
      state.user = action.payload;
      // Persist full state to localStorage
      saveSecureStateToStorage(state);
    },
    setLogin: (state, action: PayloadAction<Login | undefined>) => {
      state.login = action.payload;
      // Persist full state to localStorage
      saveSecureStateToStorage(state);
    },
    clearSecureState: (state) => {
      state.accessToken = undefined;
      state.user = undefined;
      state.login = undefined;
      // Clear from localStorage
      clearSecureStateFromStorage();
    }
  },
  extraReducers: builder => {
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      state.user = action.payload.data;
      // Persist full state to localStorage
      saveSecureStateToStorage(state);
    })
    .addCase(loginAudit.fulfilled, (state, action) => {
      state.login = action.payload.data;
      // Persist full state to localStorage
      saveSecureStateToStorage(state);
    })
  }
});

export const selectAccessToken = (state: RootState): string | undefined => state.secure.accessToken
export const selectUserInfo = (state: RootState): UserInfo | undefined => state.secure.user
export const selectLogin = (state: RootState): Login | undefined => state.secure.login

export const { setAccessToken, setUserInfo, setLogin, clearSecureState } = secureSlice.actions;
export default secureSlice.reducer
